import type { BlogPostPreview } from "@compass/shared/schema/api/resources";
import {
    formatPreviewDuration,
    formatPreviewViewCount,
    getBlogPostPreviewThumbnail,
    isEmbedPreview,
    isVideoPreview,
} from "@compass/shared/utils";
import { Link } from "@tanstack/react-router";
import { Eye, Play } from "lucide-react";
import { ReactNode, useState } from "react";
import { ReactComponent as Logo } from "assets/img/logo-icon.svg";

type Variant = "compact" | "hero";

interface PreviewMediaCardProps {
    preview: BlogPostPreview | null | undefined;
    title: string;
    variant?: Variant;
    /**
     * When set, the non-interactive image / fallback branch wraps its content
     * in a `<Link>` to `/p/$slug`. Interactive branches (embeds, video,
     * podcast) ignore this — wrapping interactive controls in an anchor is
     * invalid HTML and the interactive surface IS the click target.
     */
    linkSlug?: string;
}

const heightClass = (variant: Variant) => (variant === "hero" ? "h-[330px]" : "h-40");

export const PreviewMediaCard = ({
    preview,
    title,
    variant = "compact",
    linkSlug,
}: PreviewMediaCardProps) => {
    const [activated, setActivated] = useState(false);
    const thumbnail = getBlogPostPreviewThumbnail(preview);
    const isEmbed = isEmbedPreview(preview);
    const isVideo = isVideoPreview(preview);
    const stats = preview && "stats" in preview ? preview.stats : null;
    const isApplePodcast = preview?.type === "apple-podcast-embed";
    const playableEmbed = isEmbed && Boolean(preview && "embedSrc" in preview && preview.embedSrc);

    const wrapInLink = (content: ReactNode, className: string): ReactNode =>
        linkSlug ? (
            <Link to="/p/$slug" params={{ slug: linkSlug }} className={className}>
                {content}
            </Link>
        ) : (
            <div className={className}>{content}</div>
        );

    const stop = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    if (isApplePodcast && preview.embedSrc) {
        const isShow = preview.kind === "show";
        return (
            <div
                className={`relative w-full overflow-hidden rounded-2xl bg-white ${variant === "hero" ? "" : "border border-[#c8c8c8]"}`}
                onClick={stop}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") stop(e);
                }}
                role="presentation"
            >
                <iframe
                    src={preview.embedSrc}
                    title={`Apple Podcasts: ${title}`}
                    height={isShow ? 450 : 175}
                    className="w-full"
                    frameBorder={0}
                    allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
                    sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                    referrerPolicy="strict-origin-when-cross-origin"
                    loading="lazy"
                />
            </div>
        );
    }

    // YouTube/Vimeo lite-pattern: thumbnail until clicked, then iframe.
    if (
        (preview?.type === "youtube-embed" || preview?.type === "vimeo-embed") &&
        preview.embedSrc
    ) {
        if (activated) {
            const autoplaySep = preview.embedSrc.includes("?") ? "&" : "?";
            const src = `${preview.embedSrc}${autoplaySep}autoplay=1`;
            return (
                <div
                    className={`relative ${heightClass(variant)} w-full overflow-hidden rounded-xl bg-black`}
                    onClick={stop}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") stop(e);
                    }}
                    role="presentation"
                >
                    <iframe
                        src={src}
                        title={title}
                        className="absolute inset-0 h-full w-full"
                        frameBorder={0}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        referrerPolicy="strict-origin-when-cross-origin"
                        loading="lazy"
                    />
                </div>
            );
        }
        return (
            <button
                type="button"
                onClick={(e) => {
                    stop(e);
                    setActivated(true);
                }}
                aria-label={`Play video: ${title}`}
                className={`relative ${heightClass(variant)} w-full cursor-pointer overflow-hidden rounded-xl border-0 bg-gray-100 p-0`}
            >
                {thumbnail ? (
                    <img src={thumbnail} alt="" className="h-full w-full object-cover" />
                ) : (
                    <Logo className="h-full w-full object-cover" />
                )}
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span
                        className={`flex items-center justify-center rounded-full bg-black/60 ${variant === "hero" ? "h-14 w-14" : "h-10 w-10"}`}
                    >
                        <Play
                            className={`ml-0.5 fill-white text-white ${variant === "hero" ? "h-7 w-7" : "h-5 w-5"}`}
                        />
                    </span>
                </span>
                {isVideo && stats?.viewCount != null && variant === "hero" && (
                    <span className="absolute bottom-3 left-3 flex items-center gap-1 rounded-md bg-white/90 px-2 py-1 text-xs font-medium text-gray-900">
                        <Eye className="h-4 w-4" />
                        <span>{formatPreviewViewCount(stats.viewCount)}</span>
                    </span>
                )}
                {isVideo && stats?.durationSeconds != null && variant === "hero" && (
                    <span className="absolute bottom-3 right-3 rounded-[6px] bg-gray-800/80 px-2 py-1 text-xs font-medium text-white">
                        {formatPreviewDuration(stats.durationSeconds)}
                    </span>
                )}
            </button>
        );
    }

    // Hosted video (video-url / video-asset): inline <video> with controls.
    if ((preview?.type === "video-url" || preview?.type === "video-asset") && preview.embedSrc) {
        return (
            <div
                className={`relative ${heightClass(variant)} w-full overflow-hidden rounded-xl bg-black`}
                onClick={stop}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") stop(e);
                }}
                role="presentation"
            >
                <video
                    src={preview.embedSrc}
                    controls
                    preload="metadata"
                    className="h-full w-full object-cover"
                />
            </div>
        );
    }

    // Image / fallback path. When `linkSlug` is provided, the whole tile is a
    // navigation target. The play overlay is only shown when the preview is
    // an embed *and* has a real `embedSrc` — otherwise we'd hint at playable
    // media that won't actually play.
    return wrapInLink(
        <>
            {thumbnail ? (
                <img
                    src={thumbnail}
                    alt=""
                    className={`h-full w-full ${variant === "compact" ? "border border-[#c8c8c8]" : ""} object-cover`}
                />
            ) : (
                <Logo className="h-full w-full object-cover" />
            )}
            {playableEmbed && (
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span
                        className={`flex items-center justify-center rounded-full bg-black/60 ${variant === "hero" ? "h-14 w-14" : "h-10 w-10"}`}
                    >
                        <Play
                            className={`ml-0.5 fill-white text-white ${variant === "hero" ? "h-7 w-7" : "h-5 w-5"}`}
                        />
                    </span>
                </span>
            )}
        </>,
        `relative ${heightClass(variant)} block w-full overflow-hidden rounded-xl bg-gray-100 no-underline`,
    );
};
